export const DOCUTECH_DISCLOSURE_EMAIL_TYPE = {
  Initial: 'initial',
  Reminder: 'reminder',
  ClosingReminder: 'closing_reminder',
  SendToFulfillment: 'send_to_fulfillment',
  ScannedAtFulfillment: 'scan_at_fulfillment',
} as const

export type DocutechDisclosureEmailType =
  (typeof DOCUTECH_DISCLOSURE_EMAIL_TYPE)[keyof typeof DOCUTECH_DISCLOSURE_EMAIL_TYPE]

export const DISCLOSURE_REMINDER_SCOPE = {
  Closing: 'closing',
  NonClosing: 'non-closing',
} as const

export type DisclosureReminderScope =
  (typeof DISCLOSURE_REMINDER_SCOPE)[keyof typeof DISCLOSURE_REMINDER_SCOPE]

export const TASK_TO_PACKAGE_TYPE: Record<string, string> = {
  'sign-le-disclosure': 'Initial Disclosure',
  'sign-revised-le-disclosure': 'Redisclosure',
  'sign-cd-disclosure': 'Pre-Closing',
  'sign-revised-cd-disclosure': 'Closing',
}

export const PACKAGE_TYPE_TO_TASK: Record<string, string> = Object.fromEntries(
  Object.entries(TASK_TO_PACKAGE_TYPE).map(([k, v]) => [v, k])
)

export type DocutechPackageType =
  | 'Initial Disclosure'
  | 'Redisclosure'
  | 'Pre-Closing'
  | 'Closing'

export type DocutechPacketStatus = 'signed' | 'unsigned'

type PacketBucketMeta = {
  packageType: DocutechPackageType
  packageOrder: 1 | 2 | 3 | 4
  status: DocutechPacketStatus
}

/**
 * Encompass eFolder bucket titles (verbatim `document_requests.title`) that
 * hold Docutech packets. The doubled "Unsigned Unsigned" title is the live
 * Encompass name since 2026-09; the single one still exists on older loans.
 */
export const DOCUTECH_PACKET_BUCKET_META = {
  'Initial Disclosures Unsigned': {
    packageType: 'Initial Disclosure',
    packageOrder: 1,
    status: 'unsigned',
  },
  'Initial Disclosures Unsigned Unsigned': {
    packageType: 'Initial Disclosure',
    packageOrder: 1,
    status: 'unsigned',
  },
  'Initial Disclosures Signed': {
    packageType: 'Initial Disclosure',
    packageOrder: 1,
    status: 'signed',
  },
  'Redisclosure Unsigned': {
    packageType: 'Redisclosure',
    packageOrder: 2,
    status: 'unsigned',
  },
  'Redisclosure Signed': {
    packageType: 'Redisclosure',
    packageOrder: 2,
    status: 'signed',
  },
  'Pre-Closing Unsigned': {
    packageType: 'Pre-Closing',
    packageOrder: 3,
    status: 'unsigned',
  },
  'Pre-Closing Signed': {
    packageType: 'Pre-Closing',
    packageOrder: 3,
    status: 'signed',
  },
  'Closing Docs Unsigned': {
    packageType: 'Closing',
    packageOrder: 4,
    status: 'unsigned',
  },
  'Closing Docs Signed': {
    packageType: 'Closing',
    packageOrder: 4,
    status: 'signed',
  },
} as const satisfies Record<string, PacketBucketMeta>

export type DocutechPacketBucketTitle = keyof typeof DOCUTECH_PACKET_BUCKET_META

export const DOCUTECH_PACKET_BUCKET_TITLES = Object.keys(
  DOCUTECH_PACKET_BUCKET_META
) as DocutechPacketBucketTitle[]

export function isDocutechPacketBucketTitle(
  title: string | null | undefined
): title is DocutechPacketBucketTitle {
  return (DOCUTECH_PACKET_BUCKET_TITLES as readonly string[]).includes(
    title ?? ''
  )
}
